import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from '@/components/ui/badge';
import { Trophy, TrendingUp, Target, Percent } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const Leaderboard = () => {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('successful_setups'); // 'successful_setups', 'total_profit', 'success_rate'
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/leaderboard`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      toast.error('Ошибка загрузки лидерборда');
    } finally {
      setLoading(false);
    }
  };

  const sortedLeaderboard = [...leaderboard].sort((a, b) => {
    if (sortBy === 'successful_setups') {
      return b.successful_setups - a.successful_setups;
    } else if (sortBy === 'total_profit') {
      return b.total_profit - a.total_profit;
    } else if (sortBy === 'success_rate') {
      return b.success_rate - a.success_rate;
    }
    return 0;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Header />
        <div className="pt-20 pb-8 px-4 md:px-6 max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-800 rounded w-1/4"></div>
            <div className="h-64 bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const getMedalColor = (index) => {
    if (index === 0) return 'text-yellow-400';
    if (index === 1) return 'text-gray-400';
    if (index === 2) return 'text-orange-600';
    return 'text-gray-600';
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Toaster position="top-right" />
      <Header />
      
      <div className="pt-20 pb-8 px-4 md:px-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 flex items-center gap-2">
            <Trophy className="h-8 w-8 text-[#00ff88]" />
            Лидерборд
          </h1>
          <p className="text-gray-400">Топ трейдеров с публичными сетапами</p>
        </div>

        {/* Sort Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setSortBy('successful_setups')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              sortBy === 'successful_setups'
                ? 'bg-gradient-to-r from-[#00ff88] to-[#00cc6f] text-black font-semibold'
                : 'bg-[#1a1a1a] text-gray-400 hover:bg-gray-800 border border-gray-800'
            }`}
          >
            <Target className="inline h-4 w-4 mr-2" />
            Успешные сетапы
          </button>
          <button
            onClick={() => setSortBy('total_profit')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              sortBy === 'total_profit'
                ? 'bg-gradient-to-r from-[#00ff88] to-[#00cc6f] text-black font-semibold'
                : 'bg-[#1a1a1a] text-gray-400 hover:bg-gray-800 border border-gray-800'
            }`}
          >
            <TrendingUp className="inline h-4 w-4 mr-2" />
            Общий профит
          </button>
          <button
            onClick={() => setSortBy('success_rate')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              sortBy === 'success_rate'
                ? 'bg-gradient-to-r from-[#00ff88] to-[#00cc6f] text-black font-semibold'
                : 'bg-[#1a1a1a] text-gray-400 hover:bg-gray-800 border border-gray-800'
            }`}
          >
            <Percent className="inline h-4 w-4 mr-2" />
            % успешности
          </button>
        </div>

        {/* Leaderboard List */}
        {sortedLeaderboard.length === 0 ? (
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="pt-6 text-center text-gray-400">
              <Trophy className="h-12 w-12 mx-auto mb-4 text-gray-600" />
              <p>Пока нет данных для лидерборда</p>
              <p className="text-sm mt-2">Создавайте публичные сетапы и они появятся здесь!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {sortedLeaderboard.map((user, index) => (
              <Card 
                key={user.user_id} 
                className={`bg-[#1a1a1a] border-gray-800 hover:border-[#00ff88]/50 transition-colors cursor-pointer ${
                  index < 3 ? 'border-[#00ff88]/30' : ''
                }`}
                onClick={() => navigate(`/user/${user.user_id}`)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between gap-4">
                    {/* Rank & User Info */}
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="flex-shrink-0 w-8 text-center">
                        {index < 3 ? (
                          <Trophy className={`h-6 w-6 ${getMedalColor(index)}`} />
                        ) : (
                          <span className="text-lg font-bold text-gray-600">#{index + 1}</span>
                        )}
                      </div>
                      
                      <Avatar className="h-10 w-10 flex-shrink-0">
                        <AvatarImage src={user.picture} alt={user.name} />
                        <AvatarFallback className="bg-gradient-to-r from-[#00ff88] to-[#00cc6f] text-black">
                          {user.name?.charAt(0).toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold truncate">{user.name}</p>
                        <p className="text-sm text-gray-400">
                          {user.total_setups} {user.total_setups === 1 ? 'сетап' : 'сетапов'}
                        </p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex flex-col md:flex-row items-end md:items-center gap-3 md:gap-6">
                      <div className="text-right">
                        <p className="text-sm text-gray-400">Успешные</p>
                        <p className="text-lg font-bold text-[#00ff88]">{user.successful_setups}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-400">Профит</p>
                        <p className={`text-lg font-bold ${user.total_profit >= 0 ? 'text-[#00ff88]' : 'text-red-500'}`}>
                          ${user.total_profit.toFixed(2)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-400">% успеха</p>
                        <p className="text-lg font-bold text-white">{user.success_rate.toFixed(1)}%</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
